// eyeq — dev dashboard contracts. One screen for two people (the owner and the
// external dev), answering one question per cell (locale × category ×
// sub_category): how full is it, and what material could still fill it.
//
// Layers, by trust level:
//   delivered   — registered shipments (eyeq_asset_deliveries). A constant:
//                 the client has these bytes, nothing can subtract them.
//   qa_free     — human-accepted intake frames not yet shipped.
//   url_index   — of qa_free, frames whose URL is already frozen in
//                 eyeq_asset_meta (the "original" link channel).
//   dev_*       — snapshot of the dev's offline pipeline (AI categorization +
//                 his link CSVs), pushed via /upload/devOverlay. NOT live D1
//                 data; `overlay_at` says how stale it is.
//
// Access: route class 'participant' + an email allowlist held in the worker
// secret DEV_DASH_EMAILS — the dashboard aggregates the whole collection, so
// membership alone must not open it (ADR-0042 layer 6: outsiders see 404).
import { z } from 'zod';

// ── overlay ingest (machine channel from the mini) ──────────────────────────
export const EyeqDevOverlayRowSchema = z.object({
  locale: z.string().min(1).max(16),
  category: z.string().min(1).max(64),
  sub_category: z.string().min(1).max(64),
  eval_clean: z.number().int().min(0),
  eval_total: z.number().int().min(0),
  links_linked: z.number().int().min(0),
  links_na: z.number().int().min(0),
  links_needs_human: z.number().int().min(0),
  /** Кадры ячейки, ещё НЕ залитые в R2 (конвейер держит манифест заливки и
   *  знает это точно). Опционально: старый push-скрипт работает байт-в-байт;
   *  null = не сообщалось, и состояние закрытия 'offline' не заявляется
   *  (eyeqCellClosure, ADR-0072). */
  offline_pending: z.number().int().min(0).nullable().default(null),
});
export type EyeqDevOverlayRow = z.infer<typeof EyeqDevOverlayRowSchema>;

// truncate=true on the FIRST chunk replaces the whole snapshot: the overlay is
// "current state of the offline pipeline", not an append log.
export const EyeqDevOverlayReqSchema = z.object({
  truncate: z.boolean().optional(),
  rows: z.array(EyeqDevOverlayRowSchema).min(1).max(200),
});
export type EyeqDevOverlayReq = z.infer<typeof EyeqDevOverlayReqSchema>;

export const EyeqDevOverlayResSchema = z.object({
  ok: z.literal(true),
  written: z.number().int(),
});
export type EyeqDevOverlayRes = z.infer<typeof EyeqDevOverlayResSchema>;

// ── dashboard read ──────────────────────────────────────────────────────────
// Compact keys: ~3.5k cells ride in one response, long names triple the JSON.
//   l/c/s  cell key           t   effective target ('' cells absent from the
//   ur     url_required            quota get t = null → "НЕТ КВОТЫ" in the UI)
//   d      delivered          a   accepted, not shipped (human QA)
//   au     of `a`: URL already in the index
//   ec/et  dev eval clean / total evaluated
//   lk/ln/lh  dev links: linked / legal N/A / needs_human
export const EyeqDevCellSchema = z.object({
  l: z.string(),
  c: z.string(),
  s: z.string(),
  t: z.number().int().nullable(),
  ur: z.number().int(),
  d: z.number().int(),
  a: z.number().int(),
  au: z.number().int(),
  ec: z.number().int(),
  et: z.number().int(),
  lk: z.number().int(),
  ln: z.number().int(),
  lh: z.number().int(),
});
export type EyeqDevCell = z.infer<typeof EyeqDevCellSchema>;

export const EyeqDevSummaryResSchema = z.object({
  ok: z.literal(true),
  cells: z.array(EyeqDevCellSchema),
  overlay_at: z.string(), // '' until the first push
});
export type EyeqDevSummaryRes = z.infer<typeof EyeqDevSummaryResSchema>;

export const EyeqDevAllowedResSchema = z.object({
  ok: z.literal(true),
  allowed: z.boolean(),
});
export type EyeqDevAllowedRes = z.infer<typeof EyeqDevAllowedResSchema>;

/** The allowlist decision, pure so both the gate and its test agree.
 *  Emails live in the DEV_DASH_EMAILS secret (comma-separated) — an empty or
 *  unset secret opens the dashboard to NOBODY, not everybody. */
export function eyeqDevDashAllowed(secretCsv: string | undefined, email: string): boolean {
  const list = String(secretCsv || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  const e = (email || '').trim().toLowerCase();
  return !!e && list.includes(e);
}
